import React, { useContext, useEffect, useState } from "react";
import List, { STATE } from "../../../List/index";
import SecondaryPagination from "../../../SecondaryPagination/SecondaryPagination";

import { ReactComponent as AddColorSvg } from "../../../../assets/admin/icons/add-color.svg";
import { ReactComponent as DeleteColorSvg } from "../../../../assets/admin/icons/delete-color.svg";
import { ReactComponent as DeleteDisableSvg } from "../../../../assets/admin/icons/delete-disable.svg";
import { ReactComponent as EyeSvg } from "../../../../assets/admin/icons/eye.svg";

import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SearchBox from "../../SearchBox";
import useUsers from "../../../../hooks/useUsers";
import { format } from "date-fns";
import View from "./View";
import AppContext from "../../../../AppContext";
import { toast } from "react-toastify";

const columns = [
  {
    display: "#",
    key: "no",
    textAlign: "left",
    maxWidth: 60,
  },
  {
    display: "Name",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Ratings",
    key: "score",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "Chat Supported",
    key: "chats",
    textAlign: "left",
    maxWidth: 100,
  },
  {
    display: "Joined on",
    key: "joined",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "",
    key: "status",
    textAlign: "left",
    maxWidth: 100,
  },
  {
    display: "",
    key: "actions",
    textAlign: "left",
    maxWidth: 32,
  },
];

const Staff = () => {
  const { user } = useContext(AppContext);
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [staffs, setStaffs] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [enableActions, setEnableActions] = useState(false);
  const [selections, setSelections] = useState([]);
  const [summary, setSummary] = useState(undefined);

  // * Toggles
  const [toggleView, setToggleView] = useState(false);
  const [selected, setSelected] = useState(undefined);

  const { queryStaffs, deleteStaff, queryStaffRatings, getChatSupportSummary } =
    useUsers();

  useEffect(() => {
    fetchData();
    return () => {};
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter, limit, page]);

  useEffect(() => {
    setEnableActions(selections.length > 0);
  }, [selections]);

  // * Fetch data
  const fetchData = async () => {
    const _filter = {
      $and: [{ deleted: false }, filter],
    };
    const { data, _metadata } = await queryStaffs(_filter, page, limit);

    const staffIds = data.map((d) => d._id);

    const { data: ratings } = await queryStaffRatings({
      staff: {
        $in: staffIds,
      },
    });

    let _staffs = data.map(
      ({ _id, firstname, lastname, createdOn, active }, staffIndex) => {
        const _joined = createdOn
          ? format(new Date(createdOn), "dd/MM/yyyy")
          : "-";

        const ratingFound = ratings.find((r) => r.staff === _id);

        const { score, count, reviews } = ratingFound ?? {};

        return {
          no: (_metadata.currentPage - 1) * 10 + staffIndex + 1,
          _id,
          score: score ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 14,
              }}
            >
              <StarRoundedIcon style={{ color: "#faaf00" }} />
              {score}
            </div>
          ) : (
            "-"
          ),
          chats: count ?? 0,
          reviews: reviews,
          name: `${firstname} ${lastname || ""}`,
          joined: _joined,
          status: (
            <div className="admin-users-item-status">
              <label
                className={`admin-users-item-status__label admin-users-item-status__label--${
                  active ? "active" : "inactive"
                }`}
              >
                {active ? "Active" : "Inactive"}
              </label>
            </div>
          ),
          actions: (
            <div className="admin-users-item-actions">
              <div
                className="admin-users-item-actions-action"
                onClick={() => handleView(_id)}
              >
                <EyeSvg />
              </div>
            </div>
          ),
        };
      }
    );

    setStaffs(_staffs);
    setMetadata(_metadata);
    setDataState(STATE.IDLE);

    // * Get summary
    const { data: summary } = await getChatSupportSummary();
    setSummary(summary);
  };

  const handleSelected = (row) => {
    console.log(row);
  };

  const handleOnAddSelection = (selection) => {
    setSelections((current) => [...current, selection]);
  };

  const handleOnRemoveSelection = (selection) => {
    let _selections = selections.filter((s) => s._id !== selection._id);
    setSelections(_selections);
  };

  const handleOnPageChange = (page) => setPage(page);

  const handleOnLimitChange = (limit) => {
    setPage(1);
    setLimit(limit);
  };

  const handleSearchChange = (value) => {
    if (!value) {
      setFilter({});
      return;
    }

    setPage(1);
    setFilter({
      $or: [
        {
          firstname: { $regex: value },
        },
        {
          lastname: { $regex: value },
        },
        {
          email: { $regex: value },
        },
      ],
    });
  };

  const handleAddUser = () => {
    // * handleAddUser
    setSelected(undefined);
    setToggleView(true);
  };

  const handleDeleteUser = async () => {
    if (!enableActions || selections.length === 0) return;

    // * Check if own user exist to prevent deletion
    if (selections.find((s) => s._id === user._id)) {
      toast.warn("Can't delete yourself");
      return;
    }

    const deletePromises = selections.map((s) => deleteStaff(s._id));
    await Promise.allSettled(deletePromises);

    toast.success("Deleted Staffs");

    fetchData();
  };

  const handleView = (id) => {
    setSelected(id);
    setToggleView(true);
  };

  const handleViewClose = () => {
    setSelected(undefined);
    setToggleView(false);
  };

  const handleViewSuccess = () => {
    setSelected(undefined);
    setToggleView(false);
    fetchData();
  };

  return (
    <>
      <div className="admin-users">
        {summary && (
          <div className="admin-users-summary">
            <span className="admin-users-summary__title">
              Chat Support Summary
            </span>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Daily</div>
              {summary.daily}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Weekly</div>
              {summary.weekly}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Monthly</div>
              {summary.monthly}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Yearly</div>
              {summary.yearly}
            </div>
          </div>
        )}
        <div className="admin-users-header">
          <SearchBox placeholder="Email or UID" onChange={handleSearchChange} />
          <div className="admin-users-header-actions">
            <div
              className={`admin-users-header-actions-action${
                enableActions
                  ? ""
                  : " admin-users-header-actions-action--disable"
              }`}
              style={{ color: "#F0483E" }}
              onClick={handleDeleteUser}
            >
              <div className="admin-users-header-actions-action__icon">
                {enableActions ? <DeleteColorSvg /> : <DeleteDisableSvg />}
              </div>
              <div className="admin-users-header-actions-action__text">
                Delete
              </div>
            </div>
            <div
              className={`admin-users-header-actions-action`}
              style={{ color: "#0098FF" }}
              onClick={handleAddUser}
            >
              <div className="admin-users-header-actions-action__icon">
                <AddColorSvg />
              </div>
              <div className="admin-users-header-actions-action__text">
                Add staff
              </div>
            </div>
          </div>
        </div>
        <List
          contentState={dataState}
          columns={columns}
          dataSource={staffs}
          viewable={false}
          enableSelection={true}
          enableSelectionHeader={true}
          onAddSelection={handleOnAddSelection}
          onRemoveSelection={handleOnRemoveSelection}
          onRecordClick={(e) => handleSelected(e)}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            defaultLimit={metadata?.pageSize}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
      {toggleView && (
        <View
          editing={selected}
          onClose={handleViewClose}
          onSuccess={handleViewSuccess}
        />
      )}
    </>
  );
};

Staff.propTypes = {};

export default Staff;
